import { useEffect, useState } from "react";
import { Home, Search, ExternalLink, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface Brand {
  id: string;
  name: string;
  slug: string;
}

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [attemptedBrandRoute, setAttemptedBrandRoute] = useState<string | null>(null);
  const [attemptedPage, setAttemptedPage] = useState<string | null>(null);

  useEffect(() => {
    // Detect if user was trying to access a brand route
    const path = location.pathname;
    const brandRouteMatch = path.match(/\/(brand|preview)\/([^\/]+)(\/(.+))?/);
    
    if (brandRouteMatch) {
      const attemptedSlug = brandRouteMatch[2];
      const pageRoute = brandRouteMatch[4] || 'dashboard';
      setAttemptedBrandRoute(attemptedSlug);
      setAttemptedPage(pageRoute);
    }

    // Fetch available brands
    const fetchBrands = async () => {
      try {
        const { data, error } = await supabase
          .from('brands')
          .select('id, name, slug')
          .order('name');
        
        if (!error && data) {
          setBrands(data);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();

    // SEO meta tags for 404 page
    document.title = "404 - Page Not Found";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'The page you are looking for could not be found.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'The page you are looking for could not be found.';
      document.head.appendChild(meta);
    }

    // Set robots meta tag to prevent indexing 404 pages
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }

    // Set canonical URL to home page
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', window.location.origin);

    return () => {
      // Reset title on unmount
      document.title = 'AI Health Coach';
    };
  }, [location.pathname]);

  const getBrandSuggestions = () => {
    if (!attemptedBrandRoute || brands.length === 0) return [];
    
    // Find exact match or similar brands
    return brands
      .map(brand => ({
        ...brand,
        similarity: calculateSimilarity(attemptedBrandRoute, brand.slug)
      }))
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5);
  };

  const calculateSimilarity = (str1: string, str2: string): number => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const getEditDistance = (str1: string, str2: string): number => {
    const costs = [];
    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str2.length];
  };

  const suggestions = getBrandSuggestions();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-8xl md:text-9xl font-bold text-muted mb-4">404</h1>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Page Not Found
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {attemptedBrandRoute 
              ? `The brand "${attemptedBrandRoute}" doesn't exist or the URL is incorrect.`
              : "The page you're looking for doesn't exist or has been moved."
            }
          </p>
        </div>

        {attemptedBrandRoute && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Attempted URL:</strong> {location.pathname}
              {attemptedPage && attemptedPage !== 'dashboard' && (
                <span className="block mt-1">
                  <strong>Page:</strong> {attemptedPage}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {!loading && suggestions.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Did you mean one of these brands?</CardTitle>
              <CardDescription>
                Here are some available brands that might match what you're looking for
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {suggestions.map((brand) => (
                <Button
                  key={brand.id}
                  variant="outline"
                  className="w-full justify-between group hover:border-primary"
                  onClick={() => {
                    const page = attemptedPage && attemptedPage !== 'dashboard' ? `/${attemptedPage}` : '';
                    navigate(`/brand/${brand.slug}${page}`);
                  }}
                >
                  <span className="font-medium">{brand.name}</span>
                  <div className="flex items-center gap-2 text-muted-foreground group-hover:text-primary">
                    <code className="text-xs">/brand/{brand.slug}</code>
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        {!loading && brands.length > 0 && suggestions.length === 0 && attemptedBrandRoute && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Available Brands</CardTitle>
              <CardDescription>
                Choose from one of these active brands
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {brands.slice(0, 5).map((brand) => (
                <Button
                  key={brand.id}
                  variant="outline"
                  className="w-full justify-between group hover:border-primary"
                  onClick={() => navigate(`/brand/${brand.slug}`)}
                >
                  <span className="font-medium">{brand.name}</span>
                  <div className="flex items-center gap-2 text-muted-foreground group-hover:text-primary">
                    <code className="text-xs">/brand/{brand.slug}</code>
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate("/")}
            className="gap-2"
          >
            <Home className="w-4 h-4" />
            Go Home
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <Search className="w-4 h-4" />
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
